import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Alert,
  LinearProgress,
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import { trainingFilesApi } from '../../services/trainingFilesService';
import { reportUploadError } from '../../services/errorReporter';
import { TrainingFileCategory } from '../../services/types';
import { formatBytes } from '../../utils/formatBytes';

interface TrainingFileUploadDialogProps {
  open: boolean;
  workloadId: string;
  onClose: () => void;
  onUploaded?: () => void;
  // NC-1098 (Feat 2.0): folder + run scope.
  /** Preset target folder under C:\Schulungsunterlagen (POSIX, editable). */
  defaultRelativePath?: string;
  /** When set, the upload is a run-scoped ad-hoc overlay for this run. */
  runId?: string;
  /** Optional dialog title override (e.g. run-scoped upload). */
  title?: string;
}

const ALLOWED_EXTENSIONS = [
  '.pdf', '.zip', '.csv', '.json', '.txt', '.md',
  '.xlsx', '.docx', '.sqlite', '.db', '.accdb',
  // NC-1095: CAD/BIM project + point-cloud formats for Schulungsunterlagen.
  '.rvt', '.rte', '.rfa', '.dwg', '.rcp', '.rcs',
] as const;

function hasAllowedExtension(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * NC-1098: light client-side hygiene for the target folder (the BE is the real
 * validator). Rejects traversal, absolute paths and drive letters so a bad path
 * fails fast before a SAS roundtrip.
 */
export function validateRelativePath(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null; // empty = root, allowed
  if (value.startsWith('/') || value.startsWith('\\')) {
    return 'Kein absoluter Pfad — relativ zu C:\\Schulungsunterlagen angeben.';
  }
  if (/^[a-zA-Z]:/.test(value)) {
    return 'Kein Laufwerksbuchstabe — nur ein relativer Ordnerpfad.';
  }
  if (value.replace(/\\/g, '/').split('/').some((s) => s.trim() === '..')) {
    return 'Pfad darf kein ".." enthalten.';
  }
  return null;
}

export default function TrainingFileUploadDialog({
  open,
  workloadId,
  onClose,
  onUploaded,
  defaultRelativePath,
  runId,
  title,
}: TrainingFileUploadDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<TrainingFileCategory>('unterlagen');
  const [relativePath, setRelativePath] = useState(defaultRelativePath ?? '');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Keep the target folder in sync when the dialog is (re)opened for a
  // specific tree folder — the parent passes the folder path via prop.
  useEffect(() => {
    if (open) setRelativePath(defaultRelativePath ?? '');
  }, [open, defaultRelativePath]);

  const reset = () => {
    setSelectedFile(null);
    setDescription('');
    setCategory('unterlagen');
    setRelativePath(defaultRelativePath ?? '');
    setUploadProgress(0);
    setUploadStatus('');
    setError(null);
  };

  const handleClose = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!hasAllowedExtension(file.name)) {
      setSelectedFile(null);
      setError(
        `Dateityp nicht erlaubt. Zulaessig: ${ALLOWED_EXTENSIONS.join(', ')}`
      );
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Bitte waehle eine Datei aus.');
      return;
    }

    const pathError = validateRelativePath(relativePath);
    if (pathError) {
      setError(pathError);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      setUploadStatus('Upload-URL anfordern...');
      const trimmedPath = relativePath.trim();
      const { uploadUrl, fileId } = await trainingFilesApi.requestUploadUrl({
        workloadId,
        fileName: selectedFile.name,
        description: description || undefined,
        category,
        relativePath: trimmedPath || undefined,
        runId: runId || undefined,
      });

      // NC-1095: uploadUrl is null only for kind=folder requests — a file
      // upload without a SAS URL is a server-side contract violation.
      if (!uploadUrl) {
        throw new Error('Server lieferte keine Upload-URL');
      }

      setUploadStatus('MD5-Pruefsumme berechnen...');
      const md5Hash = await trainingFilesApi.computeMD5(selectedFile);

      setUploadStatus('Datei hochladen...');
      await trainingFilesApi.uploadFile(uploadUrl, selectedFile, md5Hash, (progress) => {
        setUploadProgress(progress);
      });

      setUploadStatus('Upload bestaetigen...');
      await trainingFilesApi.confirmUpload({
        fileId,
        workloadId,
        md5Hash,
        sizeBytes: selectedFile.size,
      });

      setUploadStatus('Upload erfolgreich.');
      reset();
      onUploaded?.();
      onClose();
    } catch (err) {
      // NC-1722: the blob PUT bypasses the apiClient interceptor — report it
      // here, or it never shows up under Feedback-und-Fehler.
      reportUploadError(err, { kind: 'training-file', fileName: selectedFile.name });
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title ?? 'Trainings-Datei hochladen'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              disabled={uploading}
              startIcon={<UploadIcon />}
            >
              Datei auswaehlen
              <input
                type="file"
                hidden
                onChange={handleFileSelect}
                data-testid="training-file-input"
              />
            </Button>
            {selectedFile && (
              <Typography variant="caption" display="block" mt={1}>
                {selectedFile.name} ({formatBytes(selectedFile.size)})
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Zielordner (optional)"
              value={relativePath}
              onChange={(e) => setRelativePath(e.target.value)}
              fullWidth
              disabled={uploading}
              placeholder="z.B. Kapitel1/Uebungen"
              helperText={
                runId
                  ? 'Ordner unter C:\\Schulungsunterlagen für DIESEN Lauf. Leer = Wurzel.'
                  : 'Ordner unter C:\\Schulungsunterlagen. Leer = Wurzel. Kein ".." / Laufwerk.'
              }
              inputProps={{ 'aria-label': 'Zielordner' }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label="Kategorie"
              value={category}
              onChange={(e) => setCategory(e.target.value as TrainingFileCategory)}
              fullWidth
              disabled={uploading}
              helperText="Unterlagen = Schulungsmaterial. Konfiguration = Workload-Konfig (Post-Prozessoren, Vorlagen)."
            >
              <MenuItem value="unterlagen">Unterlagen</MenuItem>
              <MenuItem value="konfiguration">Konfiguration</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Beschreibung (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              disabled={uploading}
              helperText="z.B. 'HyperMill Manual, Stand 2026-06'"
            />
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          {uploading && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 1 }}>
                {uploadStatus}
              </Alert>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" display="block" mt={0.5} align="center">
                {Math.round(uploadProgress)}%
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Abbrechen
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={uploading || !selectedFile}
        >
          {uploading ? 'Hochladen...' : 'Hochladen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
